import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, Loader2, MapPin, Phone } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const EMPTY = { name: "", email: "", password: "", phone: "", region: "" };

const AgentDialog = ({ open, onClose, onSaved, agent }) => {
  const { branches } = useAuth();
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setF(agent ? { ...EMPTY, ...agent, password: "" } : EMPTY);
  }, [open, agent]);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.region) return toast.error("اختر الفرع");
    setBusy(true);
    try {
      if (agent) {
        const body = { name: f.name, phone: f.phone, region: f.region };
        if (f.password) body.password = f.password;
        await api.put(`/agents/${agent.id}`, body);
      } else await api.post("/agents", { ...f, email: f.email.trim() || null });
      toast.success(agent ? "تم تحديث المندوب" : "تم إنشاء حساب المندوب");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent dir="rtl" data-testid="agent-dialog">
        <DialogHeader><DialogTitle className="text-start font-heading">{agent ? "تعديل المندوب" : "مندوب جديد"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5"><Label>الاسم *</Label><Input required value={f.name} onChange={set("name")} data-testid="agent-name-input" /></div>
          <div className="space-y-1.5"><Label>رقم الجوال * (يُستخدم للدخول)</Label><Input required dir="ltr" className="text-left" placeholder="05xxxxxxxx" value={f.phone} onChange={set("phone")} data-testid="agent-phone-input" /></div>
          {!agent && <div className="space-y-1.5"><Label>البريد الإلكتروني (اختياري)</Label><Input type="email" dir="ltr" className="text-left" value={f.email || ""} onChange={set("email")} data-testid="agent-email-input" /></div>}
          <div className="space-y-1.5"><Label>{agent ? "كلمة مرور جديدة (اختياري)" : "كلمة المرور * (6 أحرف على الأقل)"}</Label><Input required={!agent} minLength={6} dir="ltr" className="text-left" value={f.password} onChange={set("password")} data-testid="agent-password-input" /></div>
          <div className="space-y-1.5"><Label>الفرع</Label>
            <div className="grid grid-cols-2 gap-2" data-testid="agent-region-input">
              {branches.map((b) => (
                <button type="button" key={b} onClick={() => setF((x) => ({ ...x, region: b }))} data-testid={`agent-branch-${b}`}
                  className={`h-10 rounded-lg border text-sm font-semibold ${f.region === b ? "bg-brand-navy text-white border-brand-navy" : "border-slate-200 hover:bg-slate-100"}`}>{b}</button>
              ))}
            </div>
          </div>
          <button disabled={busy} data-testid="agent-submit-button" className="w-full h-12 rounded-lg bg-brand-navy text-white font-bold hover:bg-brand-navy-dark flex items-center justify-center gap-2 disabled:opacity-60">
            {busy && <Loader2 size={18} className="animate-spin" />}حفظ
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default function Agents() {
  const [agents, setAgents] = useState([]);
  const [dialog, setDialog] = useState({ open: false, agent: null });
  const load = useCallback(() => api.get("/agents").then((r) => setAgents(r.data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => {
    load();
  }, [load]);

  const toggle = async (a) => {
    await api.put(`/agents/${a.id}`, { active: !a.active });
    load();
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4 rise">
        <div>
          <p className="text-xs font-bold text-brand-navy mb-1"><span className="font-mono tracking-widest">TEAM</span> · ضغط العمل</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold">المناديب</h1>
        </div>
        <button onClick={() => setDialog({ open: true, agent: null })} data-testid="add-agent-button"
          className="h-12 px-5 rounded-lg bg-brand-red text-white font-bold flex items-center gap-2 hover:bg-brand-red-dark transition-colors"><UserPlus size={18} /> إضافة مندوب</button>
      </div>
      {agents.length === 0 && <p className="text-slate-500" data-testid="agents-empty">لا يوجد مناديب بعد. أضف أول مندوب لبدء إسناد المعاينات.</p>}
      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="agents-list">
        {agents.map((a) => (
          <div key={a.id} className={`bg-white border rounded-xl p-5 ${a.active ? "border-slate-200" : "border-slate-200 opacity-60"}`} data-testid={`agent-card-${a.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-heading font-bold text-lg">{a.name}</p>
                {a.email && <p className="text-xs text-slate-500 font-mono truncate" dir="ltr">{a.email}</p>}
              </div>
              <Switch checked={a.active} onCheckedChange={() => toggle(a)} data-testid={`agent-active-switch-${a.id}`} />
            </div>
            <div className="mt-4 space-y-1.5 text-sm text-slate-600">
              <p className="flex items-center gap-2"><MapPin size={14} />{a.region || "بدون منطقة"}</p>
              <p className="flex items-center gap-2"><Phone size={14} /><span dir="ltr" className="font-mono">{a.phone || "-"}</span></p>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2">
              <div className="rounded-lg bg-slate-50 border border-slate-200 p-3"><p className="text-xs text-slate-500">مهام نشطة</p><p className="font-mono text-2xl font-bold" data-testid={`agent-active-tasks-${a.id}`}>{a.active_tasks}</p></div>
              <div className="rounded-lg bg-slate-50 border border-slate-200 p-3"><p className="text-xs text-slate-500">مكتملة</p><p className="font-mono text-2xl font-bold text-emerald-700">{a.completed_tasks}</p></div>
              <div className="col-span-2 rounded-lg bg-amber-50 border border-amber-200 p-3 flex items-center justify-between" data-testid={`agent-rating-${a.id}`}>
                <span className="text-xs text-amber-800">تقييم العملاء</span>
                <span className="font-bold text-amber-700">{a.avg_rating != null ? `★ ${a.avg_rating} / 5` : "—"} <span className="text-xs font-normal text-amber-800">({a.ratings_count} تقييم)</span></span>
              </div>
            </div>
            <button onClick={() => setDialog({ open: true, agent: a })} data-testid={`agent-edit-button-${a.id}`} className="mt-4 w-full h-10 rounded-lg border border-slate-200 text-sm font-semibold hover:bg-slate-100 transition-colors">تعديل</button>
          </div>
        ))}
      </div>
      <AgentDialog open={dialog.open} agent={dialog.agent} onClose={() => setDialog({ open: false, agent: null })} onSaved={load} />
    </div>
  );
}
